/**
 * Apps (slice 6i §7, Paper "Apps (slice 6i, Nov 1)" A1, A2): the apps this
 * person can see, the newest first, each a row with its tile, title, whose it
 * is and who can open it. Under Docs in the sidebar; in More on a phone.
 */
import { Link } from "@crew/ui/components/link";
import { AppsIcon } from "@crew/ui/components/nav-icons";
import { Spinner } from "@crew/ui/components/spinner";
import { PageTitle } from "@crew/ui/components/typography";
import { cn } from "@crew/ui/lib/utils";
import { Lock, Users } from "lucide-react";
import { errorHint } from "../../app/api";
import type { Me } from "../../app/me";
import { shortTime } from "../chats/format";
import { usePeople } from "../people/api";
import { useApps, whoseLine, type AppItem } from "./api";

/** An app's tile colour: one of four from the theme, fixed by its id. */
const TINTS = ["bg-moss", "bg-foreground", "bg-copper", "bg-[#5E7A8C]"];
const tintOf = (id: string) => TINTS[parseInt(id.slice(0, 2), 16) % TINTS.length] ?? TINTS[0];

/** The tile every app wears, in the list and in its chat card. */
export function AppTile({ id, className = "size-10" }: { id: string; className?: string }) {
  return (
    <span
      aria-hidden
      className={cn(
        "flex shrink-0 items-center justify-center rounded-[10px] text-white",
        tintOf(id),
        className,
      )}
    >
      <AppsIcon size={18} />
    </span>
  );
}

function AppRow({ app, me, names }: { app: AppItem; me: Me; names: Map<string, string> }) {
  const shared = app.sharing !== "owner" || !app.mine;
  return (
    <Link
      href={`/apps/${app.id}`}
      data-app-id={app.id}
      className="flex items-center gap-3 border-b border-border py-3.5"
    >
      <AppTile id={app.id} />
      <span className="flex min-w-0 flex-1 flex-col gap-0.5">
        <span className="truncate text-body font-medium text-foreground">{app.title}</span>
        <span className="truncate text-meta text-muted-foreground">
          {whoseLine(app, names)} · changed {shortTime(app.updated_at, me.workspace.timezone)}
        </span>
      </span>
      <span className="flex w-5 shrink-0 justify-end text-hint">
        {shared ? <Users className="size-4" /> : <Lock className="size-4" />}
      </span>
    </Link>
  );
}

export function AppsPage({ me }: { me: Me }) {
  const { data: apps, error, isPending } = useApps();
  const people = usePeople();
  const names = new Map((people.data?.people ?? []).map((p) => [p.id, p.name.split(" ")[0]]));
  return (
    <main className="flex w-full max-w-[--container-letter] flex-col gap-4 px-4 pt-6 pb-16 sm:px-6 md:px-14 md:pt-12">
      <div className="flex min-w-0 flex-col gap-1.5">
        <PageTitle>Apps</PageTitle>
        <p className="text-body text-muted-foreground">
          Things the crew built for you to use. Ask in any chat for another.
        </p>
      </div>
      {isPending ? (
        <Spinner className="mt-6 self-center" />
      ) : error ? (
        <p className="text-sm text-muted-foreground">{errorHint(error)}</p>
      ) : apps.length === 0 ? (
        <p data-apps-empty className="pt-4 text-body text-copy">
          No apps yet. Ask the crew in a chat for something you'll use again and again, like a chore
          chart, a workout log or a shared grocery list.
        </p>
      ) : (
        <div data-apps-list className="flex flex-col">
          {apps.map((app) => (
            <AppRow key={app.id} app={app} me={me} names={names} />
          ))}
        </div>
      )}
    </main>
  );
}
